/**
 * People pane — everyone attached to one retailer node: waiting registrations
 * first, then approved people with their role and what they can see. Tick rows
 * to approve or remove in bulk. Invitation codes for this retailer sit at the bottom.
 *
 * Role labels: "Associate" (DB value viewer) sees estimates in their access and
 * comments / responds; "Manager" also edits contact details, approves
 * registrations and sets colleagues' access, within their own access.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Loader2, Check, X, Trash2, Pencil, Mail, AlertTriangle, CheckSquare, Square, RefreshCw, Megaphone, ChevronDown, ChevronRight, Building2, ExternalLink, UserPlus } from 'lucide-react';
import { BulkBar } from '../../../components/admin/BulkBar';
import { ConfirmModal } from '../../../components/admin/ConfirmModal';
import { AccessEditorModal } from '../../../components/admin/AccessEditorModal';
import { AddPeopleModal } from './AddPeopleModal';
import { InviteCampaigns } from './InviteCampaigns';
import {
  fetchRetailerMembers, memberAction, deleteMember, reclassifyMember, approveMembers,
  type RetailerMemberRow, type RetailerMemberRole,
} from '../../../services/adminRetailerMembersApi';
import type { RetailerTreeNode } from '../../../services/adminRetailerTreeApi';

interface Props {
  node: RetailerTreeNode;
  nodes: RetailerTreeNode[];
  canEdit: boolean;
  onChanged: () => void;
}

export const ROLE_LABEL: Record<RetailerMemberRole, string> = { viewer: 'Associate', manager: 'Manager' };
export const ROLE_HELP: Record<RetailerMemberRole, string> = {
  viewer: 'Sees estimates in their access, comments and responds, can save an estimate on a customer\'s behalf.',
  manager: 'Everything an Associate does, plus: edits estimate contact details, approves or declines registrations, and sets colleagues\' role and access — within their own access.',
};

const SOURCE_LABEL: Record<string, string> = {
  salesforce: 'Salesforce contact', salesforce_parent: 'Salesforce (parent account)', domain: 'email domain', invite: 'invite link', self: 'join page', admin: 'added by admin',
};

const fmtDate = (iso: string | null) => (iso ? iso.replace('T', ' ').slice(0, 16) : '—');

/** Plain words for what a row can see. */
export function accessSummary(m: RetailerMemberRow, nodeName: string, isTopLevel = true): { text: string; tone: 'ok' | 'warn' | 'muted' } {
  if (m.status !== 'approved') return { text: '—', tone: 'muted' };
  if (m.scope === 'all') return { text: `Entire ${nodeName}${m.includeChildren && isTopLevel ? ', sub-retailers included' : ''} (every location, incl. ones added later)`, tone: 'ok' };
  if (m.scope === 'locations') return { text: `${m.locations.length} location${m.locations.length === 1 ? '' : 's'}: ${m.locations.map(l => l.name + (l.city ? ` · ${l.city}` : '')).join(', ')}`, tone: 'ok' };
  return { text: 'No access — its locations were moved away; edit to fix', tone: 'warn' };
}

export const PeoplePane: React.FC<Props> = ({ node, nodes, canEdit, onChanged }) => {
  const [rows, setRows] = useState<RetailerMemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [accessFor, setAccessFor] = useState<{ userId: number; retailerId: number } | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'remove' | 'reject'; ids: number[] } | null>(null);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const family = useMemo(() => [node, ...nodes.filter(n => n.parentRetailerId === node.id)], [node, nodes]);
  const familyKey = family.map(n => n.id).join(',');
  const nodeById = useMemo(() => new Map(nodes.map(n => [n.id, n])), [nodes]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const parts = await Promise.all(family.map(n => fetchRetailerMembers('all', '', n.id)));
      const all = parts.flatMap(p => p.members);
      setRows(all);
      setTicked(prev => new Set(Array.from(prev).filter(id => all.some(m => m.id === id))));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load people');
    } finally {
      setLoading(false);
    }
  }, [familyKey]); // eslint-disable-line
  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const base = f ? rows.filter(m => [m.email, m.firstName, m.lastName, m.sfAccountName, m.matchedDomain].some(v => (v ?? '').toLowerCase().includes(f))) : rows;
    const rank = (m: RetailerMemberRow) => (m.status === 'unmatched' ? 0 : m.status === 'pending' ? 0 : m.status === 'approved' ? 1 : 2);
    const order = new Map(family.map((n, i) => [n.id, i]));
    return [...base].sort((a, b) => rank(a) - rank(b) || (order.get(a.retailerId ?? -1) ?? 99) - (order.get(b.retailerId ?? -1) ?? 99) || (a.lastName ?? '').localeCompare(b.lastName ?? '') || (a.email ?? '').localeCompare(b.email ?? ''));
  }, [rows, filter, family]);

  /** One line per PERSON: their rows in this tree (one per node they hold), in the shown order. */
  const groups = useMemo(() => {
    const out: { userId: number; rows: RetailerMemberRow[] }[] = [];
    const idx = new Map<number, number>();
    for (const m of shown) {
      const i = idx.get(m.userId);
      if (i === undefined) { idx.set(m.userId, out.length); out.push({ userId: m.userId, rows: [m] }); } else out[i].rows.push(m);
    }
    return out;
  }, [shown]);
  const isOpen = (m: RetailerMemberRow) => m.status === 'pending' || m.status === 'unmatched';

  const waiting = rows.filter(isOpen);
  const allShownTicked = shown.length > 0 && shown.every(m => ticked.has(m.id));
  const toggleAll = () => setTicked(prev => { const n = new Set(prev); if (allShownTicked) shown.forEach(m => n.delete(m.id)); else shown.forEach(m => n.add(m.id)); return n; });
  /** Tick / untick every row of one person. */
  const toggleGroup = (g: { rows: RetailerMemberRow[] }) => setTicked(prev => {
    const n = new Set(prev); const on = g.rows.every(m => n.has(m.id));
    g.rows.forEach(m => { if (on) n.delete(m.id); else n.add(m.id); });
    return n;
  });

  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true); setError(null); setMessage(null);
    try {
      const msg = await fn();
      if (msg) setMessage(msg);
      await load(); onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const approveMany = (ids: number[]) => run(async () => {
    const r = await approveMembers(ids);
    setTicked(new Set());
    const why: Record<string, string> = { not_found: 'row gone', not_waiting: 'not waiting', no_retailer: 'no retailer picked', ambiguous: 'matched several retailers — pick one' };
    const skipped = r.skipped.map(s => `${rows.find(x => x.id === s.id)?.email ?? s.id}: ${why[s.reason] ?? s.reason}`);
    return `${r.members.length} approved with access to their entire retailer.${skipped.length ? ` Not approved — ${skipped.join('; ')}` : ''}`;
  });

  const removeMany = (ids: number[]) => run(async () => {
    for (const id of ids) await deleteMember(id);
    setTicked(new Set()); setConfirm(null);
    return `${ids.length} removed. They will be re-checked on their next sign-in.`;
  });

  const rejectMany = (ids: number[]) => run(async () => {
    for (const id of ids) await memberAction({ action: 'reject', id });
    setTicked(new Set()); setConfirm(null);
    return `${ids.length} declined.`;
  });

  /** Role is per node row; the table shows one select per person and applies it to every approved row they hold here. */
  const setRole = (g: { rows: RetailerMemberRow[] }, role: RetailerMemberRole) => run(async () => {
    for (const m of g.rows) if (m.status === 'approved') await memberAction({ action: 'set_role', id: m.id, role });
    return `${g.rows[0].email} is now ${ROLE_LABEL[role]}.`;
  });

  const tickedIds = Array.from(ticked);
  const tickedWaiting = tickedIds.filter(id => { const m = rows.find(r => r.id === id); return m && (m.status === 'pending' || m.status === 'unmatched'); });

  return (
    <div>
      {message && <div className="alert alert-success py-2 small d-flex align-items-start gap-2 mb-2">{message}<button type="button" className="btn-close ms-auto" style={{ fontSize: '0.6em' }} aria-label="Dismiss" onClick={() => setMessage(null)} /></div>}
      {error && <div className="alert alert-danger py-2 small d-flex align-items-center gap-2 mb-2"><AlertTriangle size={14} /> {error}</div>}

      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <div className="input-group input-group-sm" style={{ maxWidth: 320 }}>
          <span className="input-group-text"><Search size={14} /></span>
          <input className="form-control" placeholder="Filter: name, email, Salesforce account…" value={filter} onChange={e => setFilter(e.target.value)} />
          {filter && <button type="button" className="btn btn-outline-secondary" onClick={() => setFilter('')}><X size={13} /></button>}
        </div>
        <span className="small text-muted">{new Set(rows.filter(m => m.status === 'approved').map(m => m.userId)).size} approved{waiting.length ? <> · <span className="text-warning-emphasis fw-semibold">{new Set(waiting.map(m => m.userId)).size} waiting</span></> : ''}</span>
        <button type="button" className="btn btn-sm btn-outline-secondary ms-auto d-inline-flex align-items-center gap-1" disabled={loading} onClick={() => void load()}><RefreshCw size={13} /> Refresh</button>
        {canEdit && <button type="button" className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1" onClick={() => setAddOpen(true)}><UserPlus size={14} /> Add people</button>}
      </div>

      {canEdit && (
        <BulkBar count={ticked.size} noun="ticked" onClear={() => setTicked(new Set())}>
          <button type="button" className="btn btn-sm btn-success d-inline-flex align-items-center gap-1" disabled={busy || tickedWaiting.length === 0} title={tickedWaiting.length ? `Approve ${tickedWaiting.length} waiting with access to the entire ${node.name}` : 'None of the ticked rows is waiting'} onClick={() => void approveMany(tickedWaiting)}><Check size={13} /> Approve {tickedWaiting.length || ''}</button>
          <button type="button" className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1" disabled={busy || tickedWaiting.length === 0} onClick={() => setConfirm({ kind: 'reject', ids: tickedWaiting })}><X size={13} /> Decline</button>
          <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" disabled={busy} onClick={() => setConfirm({ kind: 'remove', ids: tickedIds })}><Trash2 size={13} /> Remove</button>
        </BulkBar>
      )}

      <div className="table-responsive bg-white rounded-3 border">
        <table className="table table-sm table-hover align-middle mb-0 small">
          <thead className="table-light">
            <tr>
              {canEdit && <th style={{ width: 32 }}><button type="button" className="btn btn-link p-0 text-dark" title={allShownTicked ? 'Untick all' : 'Tick all'} onClick={toggleAll}>{allShownTicked ? <CheckSquare size={16} /> : <Square size={16} />}</button></th>}
              <th>Person</th>
              <th>Member of</th>
              <th style={{ width: 130 }}>Role</th>
              <th>Access</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center text-muted py-4"><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</td></tr>
              : shown.length === 0 ? <tr><td colSpan={7} className="text-center text-muted py-4">{rows.length ? 'Nothing matches the filter.' : <>Nobody is attached to {node.name} yet. Staff who register with a matching Salesforce account, email domain or invite code appear here waiting for approval.</>}</td></tr>
              : groups.map(g => {
                const m = g.rows[0];
                const on = g.rows.every(r => ticked.has(r.id));
                const openRows = g.rows.filter(isOpen);
                const approvedRows = g.rows.filter(r => r.status === 'approved');
                const allRejected = g.rows.every(r => r.status === 'rejected');
                const role: RetailerMemberRole = g.rows.some(r => r.status === 'approved' && r.role === 'manager') ? 'manager' : 'viewer';
                const editTarget = (approvedRows[0] ?? m).retailerId ?? node.id;
                return (
                  <tr key={g.userId} className={on ? 'table-active' : openRows.length ? 'table-warning' : allRejected ? 'text-muted' : ''}>
                    {canEdit && <td onClick={() => toggleGroup(g)} style={{ cursor: 'pointer' }}>{on ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} className="text-muted" />}</td>}
                    <td>
                      <div className="fw-semibold">{[m.firstName, m.lastName].filter(Boolean).join(' ') || <span className="text-muted">(no name yet)</span>}</div>
                      <div className="text-muted d-flex align-items-center gap-1"><Mail size={11} /> {m.email}</div>
                      <div className="text-muted" style={{ fontSize: '0.85em' }}>
                        via {SOURCE_LABEL[m.source] ?? m.source}
                        {m.sfAccountName && <> · SF account “{m.sfAccountName}”{m.sfAccountMapped ? '' : ' (not yet remembered)'}</>}
                        {m.matchedDomain && <> · @{m.matchedDomain}</>}
                        {m.inviteCode && <> · invite <code>{m.inviteCode}</code></>}
                      </div>
                      {m.candidates.length > 0 && <div className="text-muted" style={{ fontSize: '0.85em' }}>Also matched: {m.candidates.map(c => c.name).join(', ')}</div>}
                      {g.rows.some(r => r.notes) && <div className="fst-italic text-secondary" style={{ fontSize: '0.85em' }}>{g.rows.map(r => r.notes).filter(Boolean).join(' · ')}</div>}
                    </td>
                    <td>
                      {g.rows.map(r => {
                        const owner = r.retailerId ? nodeById.get(r.retailerId) : null;
                        return <div key={r.id}>{!owner ? <span className="text-muted">—</span> : owner.id === node.id ? <span className="text-muted">this retailer</span> : <span>↳ {owner.name}</span>}</div>;
                      })}
                    </td>
                    <td>
                      {approvedRows.length ? (
                        <select className="form-select form-select-sm" value={role} disabled={!canEdit || busy} title={ROLE_HELP[role]} onChange={e => void setRole(g, e.target.value as RetailerMemberRole)}>
                          <option value="viewer">{ROLE_LABEL.viewer}</option>
                          <option value="manager">{ROLE_LABEL.manager}</option>
                        </select>
                      ) : <span className="text-muted">{ROLE_LABEL[m.role]}{m.roleSource === 'salesforce' ? ' (Salesforce)' : ''}</span>}
                    </td>
                    <td>
                      {g.rows.map(r => {
                        const owner = r.retailerId ? nodeById.get(r.retailerId) : null;
                        const access = accessSummary(r, owner?.name ?? node.name, !owner || owner.parentRetailerId === null);
                        if (r.status !== 'approved') return null;
                        return <div key={r.id} className={access.tone === 'warn' ? 'text-danger fw-semibold' : ''}>{access.text}</div>;
                      })}
                      {approvedRows.length === 0 && <span className="text-muted">—</span>}
                      {approvedRows.length > 0 && canEdit && <button type="button" className="btn btn-link btn-sm p-0" title="Change what this person can see" onClick={() => setAccessFor({ userId: g.userId, retailerId: editTarget })}><Pencil size={12} /> edit</button>}
                    </td>
                    <td>
                      {openRows.length ? <span className="badge text-bg-warning">waiting{g.rows.length > 1 ? ` (${openRows.length})` : ''}</span> : approvedRows.length ? <span className="badge text-bg-success">approved</span> : <span className="badge text-bg-secondary">declined</span>}
                      {m.approvedBy && <div className="text-muted" style={{ fontSize: '0.85em' }}>{m.status === 'approved' ? 'by' : 'decided by'} {m.approvedBy}<br />{fmtDate(m.approvedAt)}</div>}
                      {openRows.length > 0 && <div className="text-muted" style={{ fontSize: '0.85em' }}>registered {fmtDate(m.createdAt)}</div>}
                    </td>
                    <td className="text-end">
                      {canEdit ? (
                        <div className="d-inline-flex flex-wrap gap-1 justify-content-end">
                          {openRows.length > 0 && (
                            <>
                              <button type="button" className="btn btn-success btn-sm d-inline-flex align-items-center gap-1" disabled={busy} title="Approve with access to their entire retailer; narrow it afterwards if needed" onClick={() => void approveMany(openRows.map(r => r.id))}><Check size={13} /> Approve</button>
                              <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy} title="Decline" onClick={() => setConfirm({ kind: 'reject', ids: openRows.map(r => r.id) })}><X size={13} /></button>
                            </>
                          )}
                          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} title="Re-run the Salesforce / domain / invite matching" onClick={() => void run(async () => { await reclassifyMember(m.id); return 'Matching re-run.'; })}><RefreshCw size={13} /></button>
                          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} title={`Remove this person from ${node.name}${g.rows.length > 1 ? ' and its sub-retailers' : ''}`} onClick={() => setConfirm({ kind: 'remove', ids: g.rows.map(r => r.id) })}><Trash2 size={13} /></button>
                        </div>
                      ) : <span className="text-muted">Read-only</span>}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="small text-muted mt-2 d-flex flex-wrap gap-3">
        <span><strong>Associate</strong>: {ROLE_HELP.viewer}</span>
        <span><strong>Manager</strong>: {ROLE_HELP.manager}</span>
      </div>

      <div className="mt-4">
        <button type="button" className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center gap-1 fw-semibold" onClick={() => setInvitesOpen(o => !o)}>
          {invitesOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />} <Megaphone size={14} /> Invitation codes for {node.name}
        </button>
        {invitesOpen && <InviteCampaigns canEdit={canEdit} retailerId={node.id} />}
      </div>

      {family.length > 1 && (
        <div className="small text-muted mt-3 d-flex align-items-center gap-1"><Building2 size={12} /> People of the sub-retailers are listed here too; the Member of column says which.</div>
      )}
      <div className="small text-muted mt-1"><Link to="/admin/accounts" className="d-inline-flex align-items-center gap-1">All accounts <ExternalLink size={10} /></Link></div>

      {addOpen && (
        <AddPeopleModal node={node} family={family} onClose={() => setAddOpen(false)}
          onAdded={s => { setAddOpen(false); setMessage(`${s.added} added.${s.failed.length ? ` Not added — ${s.failed.join('; ')}` : ''}`); void load(); onChanged(); }} />
      )}
      {accessFor !== null && <AccessEditorModal userId={accessFor.userId} retailerId={accessFor.retailerId} onClose={() => setAccessFor(null)} onSaved={() => { void load(); onChanged(); }} />}

      <ConfirmModal open={!!confirm} title={confirm?.kind === 'reject' ? `Decline ${confirm.ids.length} registration${confirm.ids.length === 1 ? '' : 's'}?` : `Remove ${confirm?.ids.length} ${confirm?.ids.length === 1 ? 'person' : 'people'} from ${node.name}?`}
        confirmLabel={confirm?.kind === 'reject' ? 'Decline' : 'Remove'} variant="danger" busy={busy} onCancel={() => setConfirm(null)}
        onConfirm={() => { if (!confirm) return; void (confirm.kind === 'reject' ? rejectMany(confirm.ids) : removeMany(confirm.ids)); }}>
        {confirm?.kind === 'reject'
          ? <p className="mb-0">They will not get access to {node.name}. The row is kept as declined so they are not proposed again.</p>
          : <p className="mb-0">Their access to {node.name} is removed. They will be re-matched the next time they sign in.</p>}
      </ConfirmModal>
    </div>
  );
};

export default PeoplePane;
